import type { BriefChangeKind } from "./briefDiffTones";
import type {
  BriefAppendix,
  BriefAppendixChanges,
  BriefDocumentChanges,
  BriefFolderChanges,
  BriefMember,
  BriefVersionAppendix,
  BriefVersionDocument,
  BriefVersionFolder,
  BriefVersionPayload,
} from "./types";

/**
 * A brief compared to an earlier state of itself: one entry per document
 * lineage, plus what happened to its folders and its appendices. Pure: the compare dialog diffs
 * two confirmed payloads with it, and the tests pin it. The unconfirmed digest
 * comes from the database instead (`brief_get_unconfirmed_changes`) so the
 * badge and the confirm RPC share one definition; `diffFromChanges` folds that
 * shape into the same entries.
 */
export interface BriefDocChange {
  /** revised wins over moved: a new file that was also refiled reads as a new version. */
  kind: BriefChangeKind;
  lineage_key: string;
  filename: string | null;
  before: BriefVersionDocument | null;
  after: BriefVersionDocument | null;
  /** Refiled into another folder of the brief (a revised document may have been too). */
  moved: boolean;
}

export interface BriefDiff {
  entries: BriefDocChange[];
  added: number;
  removed: number;
  revised: number;
  /** Documents refiled into another folder, revised or not. */
  moved: number;
  unchanged: number;
  folders: BriefFolderChanges;
  appendices: BriefAppendixChanges;
}

export const NO_APPENDIX_CHANGES: BriefAppendixChanges = { added: [], removed: [], edited: [] };

/** How many appendix changes a diff holds, of every kind. */
export function appendixChangeCount(appendices: BriefAppendixChanges): number {
  return appendices.added.length + appendices.removed.length + appendices.edited.length;
}

/** Appendices by id. With no base (before any version) every one is new. */
export function diffAppendices(
  base: BriefVersionAppendix[] | null,
  target: BriefVersionAppendix[]
): BriefAppendixChanges {
  const b = base ?? [];
  const t = target;
  const before = new Map(b.map((a) => [a.id, a]));
  const after = new Map(t.map((a) => [a.id, a]));
  const sameDocs = (x: number[], y: number[]) =>
    x.length === y.length &&
    [...x].sort((m, n) => m - n).every((v, i) => v === [...y].sort((m, n) => m - n)[i]);
  return {
    added: t.filter((a) => !before.has(a.id)),
    removed: b.filter((a) => !after.has(a.id)),
    edited: t
      .filter((a) => {
        const prev = before.get(a.id);
        return !!prev && (prev.body !== a.body || !sameDocs(prev.document_ids, a.document_ids));
      })
      .map((a) => ({ before: before.get(a.id) as BriefVersionAppendix, after: a })),
  };
}

export const NO_FOLDER_CHANGES: BriefFolderChanges = {
  added: [],
  removed: [],
  renamed: [],
  moved: [],
};

/** How many folder changes a diff holds, of every kind. */
export function folderChangeCount(folders: BriefFolderChanges): number {
  return (
    folders.added.length + folders.removed.length + folders.renamed.length + folders.moved.length
  );
}

/** Where a document sits, as a path under the Brief folder ("" at its root). */
export function folderPathLabel(path: string[] | undefined): string {
  return (path ?? []).join(" / ");
}

function changeFolderPath(change: BriefDocChange): string {
  return folderPathLabel((change.after ?? change.before)?.folder_path);
}

function byFolderThenFilename(a: BriefDocChange, b: BriefDocChange): number {
  const pa = changeFolderPath(a);
  const pb = changeFolderPath(b);
  if (pa !== pb) return pa.localeCompare(pb, undefined, { sensitivity: "base" });
  return (a.filename ?? "").localeCompare(b.filename ?? "", undefined, { sensitivity: "base" });
}

function isRevised(before: BriefVersionDocument, after: BriefVersionDocument): boolean {
  return before.document_id !== after.document_id || before.content_hash !== after.content_hash;
}

function isMoved(before: BriefVersionDocument, after: BriefVersionDocument): boolean {
  return before.folder_id !== after.folder_id;
}

function tally(
  entries: BriefDocChange[],
  folders: BriefFolderChanges,
  appendices: BriefAppendixChanges
): BriefDiff {
  const sorted = [...entries].sort(byFolderThenFilename);
  return {
    entries: sorted,
    added: sorted.filter((e) => e.kind === "added").length,
    removed: sorted.filter((e) => e.kind === "removed").length,
    revised: sorted.filter((e) => e.kind === "revised").length,
    moved: sorted.filter((e) => e.moved).length,
    unchanged: sorted.filter((e) => e.kind === "unchanged").length,
    folders,
    appendices,
  };
}

/** Folders by id; with no base (before any version) there is nothing to compare. */
export function diffFolders(
  base: BriefVersionFolder[] | null,
  target: BriefVersionFolder[]
): BriefFolderChanges {
  if (!base) return NO_FOLDER_CHANGES;
  const before = new Map(base.map((f) => [f.id, f]));
  const after = new Map(target.map((f) => [f.id, f]));
  const byPath = (a: BriefVersionFolder, b: BriefVersionFolder) =>
    folderPathLabel(a.path).localeCompare(folderPathLabel(b.path));
  return {
    added: target.filter((f) => !before.has(f.id)).sort(byPath),
    removed: base.filter((f) => !after.has(f.id)).sort(byPath),
    renamed: target
      .filter((f) => before.has(f.id) && before.get(f.id)?.name !== f.name)
      .map((f) => ({ before: before.get(f.id) as BriefVersionFolder, after: f })),
    moved: target
      .filter((f) => before.has(f.id) && before.get(f.id)?.parent_id !== f.parent_id)
      .map((f) => ({ before: before.get(f.id) as BriefVersionFolder, after: f })),
  };
}

function documentChangeKind(
  before: BriefVersionDocument | null,
  after: BriefVersionDocument | null,
  moved: boolean
): BriefChangeKind {
  if (!before) return "added";
  if (!after) return "removed";
  if (isRevised(before, after)) return "revised";
  return moved ? "moved" : "unchanged";
}

function documentChangeFilename(
  before: BriefVersionDocument | null,
  after: BriefVersionDocument | null
): string | null {
  return (after ?? before)?.filename ?? null;
}

function documentChange(
  key: string,
  before: BriefVersionDocument | null,
  after: BriefVersionDocument | null
): BriefDocChange {
  const moved = !!after && !!before && isMoved(before, after);
  return {
    kind: documentChangeKind(before, after, moved),
    lineage_key: key,
    filename: documentChangeFilename(before, after),
    before,
    after,
    moved,
  };
}

/** `base` null means "before any confirmed version": everything is an addition. */
export function diffBriefPayloads(
  base: BriefVersionPayload | null,
  target: BriefVersionPayload
): BriefDiff {
  const previous: {
    documents: BriefVersionDocument[];
    folders: BriefVersionFolder[] | null;
    appendices: BriefVersionAppendix[] | null;
  } = base ?? { documents: [], folders: null, appendices: null };
  const before = new Map(previous.documents.map((d) => [d.lineage_key, d]));
  const after = new Map(target.documents.map((d) => [d.lineage_key, d]));
  const keys = new Set([...before.keys(), ...after.keys()]);
  const entries = Array.from(keys, (key) =>
    documentChange(key, before.get(key) ?? null, after.get(key) ?? null)
  );
  return tally(
    entries,
    diffFolders(previous.folders, target.folders),
    diffAppendices(previous.appendices, target.appendices)
  );
}

/** The database's unconfirmed diff, folded into the same entry shape. */
export function diffFromChanges(
  changes: BriefDocumentChanges,
  unchanged: BriefVersionDocument[] = []
): BriefDiff {
  const entries: BriefDocChange[] = [
    ...changes.added.map((d) => ({
      kind: "added" as const,
      lineage_key: d.lineage_key,
      filename: d.filename,
      before: null,
      after: d,
      moved: false,
    })),
    ...changes.removed.map((d) => ({
      kind: "removed" as const,
      lineage_key: d.lineage_key,
      filename: d.filename,
      before: d,
      after: null,
      moved: false,
    })),
    ...changes.changed.map((c) => ({
      kind: (c.revised ? "revised" : c.moved ? "moved" : "unchanged") as BriefChangeKind,
      lineage_key: c.after.lineage_key,
      filename: c.after.filename ?? c.before.filename,
      before: c.before,
      after: c.after,
      moved: c.moved,
    })),
    ...unchanged.map((d) => ({
      kind: "unchanged" as const,
      lineage_key: d.lineage_key,
      filename: d.filename,
      before: d,
      after: d,
      moved: false,
    })),
  ];
  return tally(entries, changes.folders, changes.appendices);
}

/** The live set as a snapshot document list — what a confirm would record now. */
export function membersToVersionDocuments(members: BriefMember[]): BriefVersionDocument[] {
  return members.map((m) => ({
    lineage_key: m.lineage_key,
    document_id: m.document_id,
    filename: m.filename,
    version: m.version,
    content_hash: m.content_hash,
    content_type: m.content_type,
    size: m.size,
    folder_id: m.folder_id,
    folder_path: m.folder_path,
  }));
}

/** The flat list `useCustomFolders` returns (`DocumentFolder` rows). */
interface FlatFolder {
  id: string | number;
  parent_id: string | number | null;
  name?: string;
}

/**
 * The Brief folder's subtree as a version records it, from the project's flat
 * folder list: the root first (parent null, path []), then every descendant
 * with its path of names under the root.
 */
export function subtreeVersionFolders(folders: FlatFolder[], rootId: number): BriefVersionFolder[] {
  const byParent = new Map<string, FlatFolder[]>();
  for (const f of folders) {
    if (f.parent_id == null) continue;
    const key = String(f.parent_id);
    byParent.set(key, [...(byParent.get(key) ?? []), f]);
  }
  const root = folders.find((f) => String(f.id) === String(rootId));
  if (!root) return [];
  const out: BriefVersionFolder[] = [
    { id: Number(root.id), parent_id: null, name: root.name ?? "", path: [] },
  ];
  const seen = new Set([String(root.id)]);
  const walk = (parentId: string, path: string[]) => {
    const children = [...(byParent.get(parentId) ?? [])].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "")
    );
    for (const child of children) {
      if (seen.has(String(child.id))) continue;
      seen.add(String(child.id));
      const name = child.name ?? "";
      const childPath = [...path, name];
      out.push({
        id: Number(child.id),
        parent_id: Number(parentId),
        name,
        path: childPath,
      });
      walk(String(child.id), childPath);
    }
  };
  walk(String(root.id), []);
  return out;
}

/** A live appendix as a version would record it. */
export function toVersionAppendix(appendix: BriefAppendix): BriefVersionAppendix {
  return {
    id: appendix.id,
    title: appendix.title,
    body: appendix.body,
    author_kind: appendix.author_kind,
    author_id: appendix.author_id,
    author_name: appendix.author_name,
    confirmed_by: appendix.confirmed_by,
    confirmed_by_name: appendix.confirmed_by_name,
    source_finding_id: appendix.source_finding_id,
    created_at: appendix.created_at,
    modified_at: appendix.modified_at,
    document_ids: appendix.documents.map((d) => d.id).sort((a, b) => a - b),
  };
}

/** The live brief as a payload, for a compare against a confirmed version. */
export function livePayload(
  members: BriefMember[],
  folders: BriefVersionFolder[],
  appendices: BriefAppendix[]
): BriefVersionPayload {
  return {
    captured_at: new Date().toISOString(),
    since_version_id: null,
    documents: membersToVersionDocuments(members),
    folders,
    appendices: appendices.map(toVersionAppendix),
    changes: {
      added: [],
      removed: [],
      changed: [],
      folders: NO_FOLDER_CHANGES,
      appendices: NO_APPENDIX_CHANGES,
    },
  };
}

/**
 * How many things changed since the latest version, as the database reports
 * it — enough to know whether the current brief IS that version.
 */
export function unconfirmedChangeCount(changes: BriefDocumentChanges): number {
  return (
    changes.added.length +
    changes.removed.length +
    changes.changed.length +
    folderChangeCount(changes.folders) +
    appendixChangeCount(changes.appendices)
  );
}
